import type { PaidMemberBenefitView } from '@yingbaba/types';

export const memberStatus: Record<string, string> = {
  free: '免费用户',
  active: '有效会员',
  expired: '已到期',
  revoked: '已撤销',
  suspended: '归属已停用',
  scheduled: '待生效',
  pending: '待核款',
  paid: '已核款',
  payment_review: '已到账·待核查',
  refunded: '已退款',
  cancelled: '已取消',
  draft: '草稿 / 暂停售卖',
  archived: '已归档',
};

// Never round a monetary input: 0.001 yuan is invalid, not a one-cent payment.
export function parseMemberMoney(input: string): number {
  const value = input.trim();
  if (!/^(?:0|[1-9]\d{0,6})(?:\.\d{1,2})?$/.test(value))
    throw new Error('金额须为人民币元，最多两位小数，不支持科学计数法。');
  const [whole, fraction = ''] = value.split('.');
  const result = Number(whole) * 100 + Number(fraction.padEnd(2, '0'));
  if (result < 1 || result > 100_000_000) throw new Error('金额须大于零且不超过 1,000,000 元。');
  return result;
}

export function memberBenefitText(b: PaidMemberBenefitView): string {
  if (b.value_type === 'boolean') return `${b.label}：${b.value === true ? '已配置' : '不包含'}`;
  if (b.value_type === 'discount_bps') return `${b.label}：优惠 ${Number(b.value) / 100}%`;
  const cycle = b.reset_period === 'day' ? ' / 日' : b.reset_period === 'month' ? ' / 月' : '';
  return `${b.label}：${b.value}${b.unit ? ` ${b.unit}` : ''}${cycle}`;
}

// Retain a key for an uncertain response, including across page reloads. Store
// only the caller's operation scope and a fingerprint; never credentials.
const pendingKeys = new Map<string, { fingerprint: string; key: string }>();
export async function memberCommandKey(scope: string, payload: unknown): Promise<string> {
  const raw = new TextEncoder().encode(JSON.stringify(payload));
  const hash = await crypto.subtle.digest('SHA-256', raw);
  const fingerprint = Array.from(new Uint8Array(hash), (n) => n.toString(16).padStart(2, '0')).join(
    '',
  );
  const memory = pendingKeys.get(scope);
  if (memory?.fingerprint === fingerprint) return memory.key;
  const storageKey = `yb.member.command:${scope}`;
  try {
    const old = JSON.parse(sessionStorage.getItem(storageKey) || 'null');
    if (old?.fingerprint === fingerprint && typeof old.key === 'string') {
      pendingKeys.set(scope, old);
      return old.key;
    }
  } catch {
    /* Storage is optional; the backend remains authoritative. */
  }
  const key = crypto.randomUUID();
  pendingKeys.set(scope, { fingerprint, key });
  try {
    sessionStorage.setItem(storageKey, JSON.stringify({ fingerprint, key }));
  } catch {
    /* Private mode */
  }
  return key;
}
export function clearMemberCommand(scope: string) {
  pendingKeys.delete(scope);
  try {
    sessionStorage.removeItem(`yb.member.command:${scope}`);
  } catch {
    /* Private mode */
  }
}
